import React from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import AppBar from "@material-ui/core/AppBar";
import Badge from "@material-ui/core/Badge";
import LinearProgress from "@material-ui/core/LinearProgress";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Toolbar from "@material-ui/core/Toolbar";

import BigNumber from "bignumber.js";
import { Row } from "react-data-grid";

import SummaryView from "./summaryview";
import { GridColumns } from "./barcodesplitgridsetup";
import { EditGridColumns } from "./barcodespliteditgridsetup";
import { NewGridColumns } from "./barcodesplitnewgridsetup";

import "react-splitter-layout/lib/index";
import Splitter from "m-react-splitters";
import "m-react-splitters/lib/splitters.css";
import "../../lib/custom/splitter.css";

import Grid from "../../lib/components/grid";
import { SnackContext } from "../../lib/context/SnackContext";
import { stock } from "../../lib/api/stock";
import { barcodessplitProcess, deleteBarcodesByID, insertBarcodeSplits } from "../../lib/api/barcodesplit";
import { OrangeButton, RedButton } from "../../lib/components/ColorButtons";
import ConfirmationDialog from "../../lib/components/confirmation";
import { BarcodeSplitForm } from "./split-form";
import { DialogInformation } from "../../lib/components/dialoginformation";
import { GenerateErrorMessage } from "../../lib/helpers/string_methods";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "calc(100vh - 100px)",
      position: "relative",
    },
    gridWrapper: {
      position: "relative",
      height: "100%",
    },
    gridToolbar: {
      position: "absolute",
      left: 0,
      top: 0,
      width: "100%",
      height: "60px",
      paddingLeft: "10px",
      paddingRight: "10px",
      display: "flex",
      alignItems: "center",
    },
    totalRowStyle: {
      background: "#ddd",
      textAlign: "right",
      fontWeight: "bold",
      color: "black",
    },
    totalRowStyleFixed: {
      position: "sticky",
      bottom: 0,
      background: "#ddd",
      textAlign: "right",
      fontWeight: "bold",
      color: "black",
    },
    lastRowStyle: {
      display: "flex",
      flexDirection: "column-reverse",
      color: "black",
    },
    boldRow: {
      fontWeight: "bold",
    },
    editRow: {
      "& div:nth-child(19)": {
        fontWeight: "bold",
        color: "green",
      },
      "& div:nth-child(20)": {
        fontWeight: "bold",
        color: "green",
      },
    },
    greenRow: {
      "& div": {
        color: "green",
      },
    },
    toolbarWrapper: {
      position: "relative",
    },
    toolbar: {
      position: "absolute",
      left: 0,
      width: "100%",
    },
  });

type BarcodeSplitProps = {} & WithStyles<typeof styles>;

const availableItems_tabIndex = 0;
const selectedItems_tabIndex = 1;

export class BarcodeSplitUnstyled extends React.Component<BarcodeSplitProps, any> {
  state = {
    classes: undefined,
    stock: [],
    loading: true,
    selectedRows: [],
    tabIndex: 0,
    newSplitBarcodes: [],
    minGridHeight: 0,
    minGridHeightSecond: 0,
    rowSelectedIdx: undefined,
    columnSelectedIdx: undefined,
    columnsLength: undefined,
    isOpen: false,
    showSummaryView: false,
    summaryData: [],
    submittingNewStock: false,
    barcodeExistsConfirmationOpen: false,
    newBarcode: "",
    newGrossWeight: "",
    existingBarcodeData: [],
    confirmDelete: false,
    weightRequiredDialog: false,
  };

  constructor(props) {
    super(props);
    this.state.classes = props.classes;
  }

  static contextType = SnackContext;
  context!: React.ContextType<typeof SnackContext>;

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    this.setState({ loading: true });
    return await stock.all().then((stockData) => {
      stockData.map((item) => {
        if (item.qx_deleted == "0") {
          return { ...item, age: item.age ? item.age : 0, ageAtLocation: item.ageAtLocation ? item.ageAtLocation : 0, exists: false };
        }
        return { ...item, exists: false };
      });
      this.setState({ stock: stockData, loading: false }, () => {
        this.setGridHeights();
      });
    });
  };

  a11yProps(index: any) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  handleTabChange = (event: React.ChangeEvent<{}>, newTabIndexValue: number) => {
    this.setState({ tabIndex: newTabIndexValue });
  };

  calculatePalletSize = (data, row) => {
    const total_cartons = data.reduce((res, item) => {
      if (!isNaN(item.cartons_to_split)) {
        res = res + parseFloat(item.cartons_to_split || 0);
      }
      return res;
    }, 0);
    const cartons = new BigNumber(1).dividedBy(new BigNumber(total_cartons));
    const result = new BigNumber(row.cartons_to_split || 0).times(cartons).toNumber();
    return result;
  };

  calculateGrossWeight = (data, row) => {
    const total_cartons = data.reduce((res, item) => {
      res = new BigNumber(res).plus(new BigNumber(item.noCartons || 0)).toNumber();
      return res;
    }, 0);

    const cartons = new BigNumber(row.newWeight || 0).dividedBy(new BigNumber(total_cartons));
    const result = new BigNumber(row.noCartons || 0).times(cartons).toNumber();
    return result;
  };

  handleRowSelect = (row) => {
    const exists = this.state.selectedRows.find((item) => item.barcode == row.barcode);

    if (exists) {
      const selectedRowsFiltered = this.state.selectedRows.filter((item) => item.barcode != row.barcode);
      // Need to do seperate functions (filter & map) to calculate new pallet sizes
      const newSplitBarcodesFiltered = this.state.newSplitBarcodes.filter((item) => item.old_barcode != row.barcode);
      const newSplitBarcodesData = newSplitBarcodesFiltered.map((item) => ({
        ...item,
        palletSize: this.calculatePalletSize(newSplitBarcodesFiltered, item),
        grossWeight: this.calculateGrossWeight(newSplitBarcodesFiltered, item),
      }));

      this.setState({ selectedRows: selectedRowsFiltered, newSplitBarcodes: newSplitBarcodesData });
      this.handleApplyBarcode({ barcode: this.state.newBarcode, newWeight: this.state.newGrossWeight }, newSplitBarcodesData);
    } else {
      const rowData = this.state.stock.reduce((arr, item) => {
        if (item.barcode == row.barcode) {
          arr.push({ ...item, exists: row.exists });
        }
        return arr;
      }, []);

      const data = [...this.state.selectedRows, ...rowData].map((item) => ({
        ...item,
        original_noCartons: item.noCartons,
        new_weight: item.new_weight || 0,
        cartons_to_split: item.cartons_to_split || 0,
      }));

      const newSplitRow = rowData.map((item) => ({
        ...item,
        barcode: this.state.newBarcode,
        old_barcode: item.barcode,
        noCartons: item.noCartons,
        grossWeight: item.exists ? item.grossWeight : 0,
        palletSize: item.exists ? item.palletSize : 0,
        newWeight: this.state.newGrossWeight,
        cartons_to_split: row.exists ? item.noCartons : 0,
      }));

      const newSplitBarcodesData = [...this.state.newSplitBarcodes, ...newSplitRow];
      this.setState({ selectedRows: data, newSplitBarcodes: newSplitBarcodesData });
      this.handleApplyBarcode({ barcode: this.state.newBarcode, newWeight: this.state.newGrossWeight }, newSplitBarcodesData);
    }
  };

  clearSelected = () => {
    this.setState({ selectedRows: [], newSplitBarcodes: [], tabIndex: 0 });
  };

  handleChange = (property, values: any[]) => {
    const selectedRowsData = values.map((item) => {
      if (item.exists) {
        return {
          ...item,
          new_weight: 0,
          cartons_to_split: 0,
          noCartons: item.noCartons,
          grossWeight: item.grossWeight,
        };
      } else {
        return {
          ...item,
          grossWeight: item.grossWeight,
          noCartons: new BigNumber(item.original_noCartons).minus(new BigNumber(item.cartons_to_split || 0)).toNumber(),
        };
      }
    });

    const newSplitBarcodesData = values.map((row) => ({
      ...row,
      old_barcode: row.barcode,
      barcode: this.state.newBarcode,
      newWeight: this.state.newGrossWeight,
      noCartons: row.exists ? row.noCartons : row.cartons_to_split,
      cartons_to_split: row.exists ? row.noCartons : row.cartons_to_split,
    }));

    const calculated = newSplitBarcodesData.map((row) => ({
      ...row,
      palletSize: this.calculatePalletSize(newSplitBarcodesData, row),
      grossWeight: this.calculateGrossWeight(newSplitBarcodesData, row),
    }));

    this.setState({ newSplitBarcodes: calculated, selectedRows: selectedRowsData });
  };

  setGridHeights = () => {
    const primarysplit = document.getElementsByClassName("splitter horizontal");
    if (primarysplit && (primarysplit || [])[0]) {
      this.setState({ minGridHeight: primarysplit[0].children[0].clientHeight - 70, minGridHeightSecond: primarysplit[0].children[2].clientHeight - 120 });
    }
  };

  handleApplyBarcode = (values: { barcode: string; newWeight: any }, splitRows: any[]) => {
    const data = splitRows.map((row) => ({
      ...row,
      barcode: values.barcode,
      palletSize: this.calculatePalletSize(splitRows, row),
      grossWeight: this.calculateGrossWeight(splitRows, { ...row, newWeight: values.newWeight }),
    }));
    this.setState({ newSplitBarcodes: data });
  };

  handleSelectedRowChange = (row, column) => {
    this.setState({ rowSelectedIdx: row.rowIdx, columnSelectedIdx: column.selectedColumn, columnsLength: column.columnsLength - 1 }); // minus one because index starts at 0
  };

  handleKeyDown = (event, gridRef) => {
    const enterKey = 13;
    const tabKey = 9;

    if (event.which === enterKey || event.which === tabKey) {
      event.preventDefault();

      const lastEditableColumn = this.state.columnsLength; // minus two because last two rows are not editable
      const tagName = event.target.tagName && event.target.tagName.toLowerCase();

      if (tagName == "div") {
        gridRef.selectCell({ idx: this.state.columnSelectedIdx, rowIdx: this.state.rowSelectedIdx }, true);
      }
      //
      else {
        if (this.state.columnSelectedIdx == lastEditableColumn) {
          this.setState({ rowSelectedIdx: this.state.rowSelectedIdx + 1, columnSelectedIdx: 16 });
          setTimeout(() => {
            gridRef.selectCell({ idx: 16, rowIdx: this.state.rowSelectedIdx }, true);
          }, 200);
        }
        //
        else {
          this.setState({ columnSelectedIdx: this.state.columnSelectedIdx + 1 });
          setTimeout(() => {
            gridRef.selectCell({ idx: this.state.columnSelectedIdx, rowIdx: this.state.rowSelectedIdx }, true);
          }, 200);
        }
      }
    }
  };

  handleGenerateStock = async () => {
    this.setState({ loading: true, summaryData: this.state.newSplitBarcodes, submittingNewStock: true });

    try {
      const barcodes = this.state.newSplitBarcodes.map((item, idx) => ({
        barcode: item.barcode,
        barcode_id: item.barcode_id,
        sequence: idx + 1,
        cartons: parseFloat((item.exists ? 0 : item.noCartons || "0").toString().replaceAll(",", "")),
        pallet_size: parseFloat((item.palletSize || "0").toString().replaceAll(",", "")),
        weight: parseFloat((item.grossWeight || "0").toString().replaceAll(",", "")),
        updateweight: parseFloat((item.exists ? item.grossWeight : item.new_weight).toString().replaceAll(",", "")),
      }));

      await insertBarcodeSplits(barcodes);
      await barcodessplitProcess();

      this.setState({
        showSummaryView: true,
        selectedRows: [],
        newSplitBarcodes: [],
        tabIndex: 0,
        submittingNewStock: false,
        newBarcode: undefined,
        newGrossWeight: undefined,
        existingBarcodeData: [],
        loading: false,
      });
    } catch (error) {
      const err = GenerateErrorMessage(error, "Failed to split barcode");
      this.context.updateSnack({ show: true, color: "red", message: err });
    }
    this.setState({ loading: false, submittingNewStock: false });
  };

  rowRenderer = (props, classes) => {
    const { row } = props;
    const fontWeight = this.state.selectedRows && this.state.selectedRows.find((item) => item.barcode == row.barcode) && classes.boldRow;

    return <Row {...props} className={fontWeight} onDoubleClickCapture={() => this.handleRowSelect({ ...row, exists: false })} />;
  };

  editRowRenderer = (props, classes) => {
    const { row } = props;
    const existsStyle = !row.exists ? classes.editRow : classes.greenRow;
    return <Row {...props} className={existsStyle} />;
  };

  newBarcodesRowRenderer = (props, classes) => {
    const { row } = props;
    const existsStyle = row.exists && classes.greenRow;
    return <Row {...props} className={existsStyle} />;
  };

  availableFilteredRows: any[];
  handleAvailableFilteredRows = (rows: any[]) => {
    this.availableFilteredRows = rows;
  };

  availableSelectAll = () => {
    const { selectedRows, newSplitRows } = this.availableFilteredRows.reduce(
      ({ selectedRows, newSplitRows }, item) => {
        selectedRows.push({ ...item, original_noCartons: item.noCartons, new_weight: item.new_weight || 0, cartons_to_split: item.cartons_to_split || 0 });
        newSplitRows.push({
          ...item,
          barcode: this.state.newBarcode,
          old_barcode: item.barcode,
          noCartons: 0,
          grossWeight: 0,
          palletSize: 0,
          newWeight: this.state.newGrossWeight,
        });
        return { selectedRows, newSplitRows };
      },
      {
        selectedRows: [],
        newSplitRows: [],
      },
    );

    this.setState({ selectedRows: [...this.state.selectedRows, ...selectedRows], newSplitBarcodes: [...this.state.newSplitBarcodes, ...newSplitRows] });
  };

  clearAvailableSelectedRows = () => {
    this.setState({ selectedRows: [], newSplitBarcodes: [], existingBarcodeData: [] });
  };

  submitGenerateStock = () => {
    if (this.state.newSplitBarcodes.some((b) => !b.exists && (b.new_weight === "" || isNaN(b.new_weight)))) {
      this.setState({ weightRequiredDialog: true });
      return;
    }

    if (this.state.newSplitBarcodes.some((b) => !b.barcode || b.barcode === "")) {
      this.context.updateSnack({ show: true, color: "red", message: "Barcode missing!" });
      return;
    }

    const noCartonsToSplit = this.state.newSplitBarcodes.filter((item) => !item.noCartons || item.noCartons == 0);

    if (noCartonsToSplit.length > 0) {
      this.setState({ isOpen: true });
    } else {
      this.handleGenerateStock();
    }
  };

  handleCloseConfirmation = () => {
    this.setState({ isOpen: false });
  };

  handleConfirmConfirmation = () => {
    this.handleCloseConfirmation();
    this.handleGenerateStock();
  };

  handleCloseSummaryView = () => {
    this.loadData();
    this.setState({ showSummaryView: false, summaryData: [] });
  };

  handleCloseBarcodeExistsConfirmation = () => {
    this.handleApplyBarcode({ barcode: this.state.newBarcode, newWeight: this.state.newGrossWeight }, this.state.newSplitBarcodes);
    this.setState({ barcodeExistsConfirmationOpen: false });
  };

  handleConfirmBarcodeExistsConfirmation = () => {
    this.state.existingBarcodeData.forEach((row) => {
      this.handleRowSelect({ ...row, cartons_to_split: row.noCartons });
    });
    this.setState({ barcodeExistsConfirmationOpen: false, existingBarcodeData: [] });
  };

  fruitSpecCompare = (itemA, itemB) => {
    if (
      itemA["barcode"] == itemB["barcode"] &&
      itemA["commodityCode"] == itemB["commodityCode"] &&
      itemA["varietyCode"] == itemB["varietyCode"] &&
      itemA["producerId"] == itemB["producerId"] &&
      itemA["farmNumber"] == itemB["farmNumber"] &&
      itemA["gradeCode"] == itemB["gradeCode"] &&
      itemA["countCode"] == itemB["countCode"] &&
      itemA["packCode"] == itemB["packCode"] &&
      itemA["markCode"] == itemB["markCode"] &&
      itemA["noCartons"] == itemB["noCartons"] &&
      itemA["organization"] == itemB["organization"]
    ) {
      return true;
    }
    return false;
  };

  handleDeleteSelectedBarcodes = () => {
    this.setState({ confirmDelete: true });
  };

  handleCloseConfirmDelete = () => {
    this.setState({ confirmDelete: false });
  };

  handleConfirmDeleteSelectedBarcodes = async () => {
    try {
      this.setState({ loading: true, confirmDelete: false });
      const barcodesToDelete = this.state.selectedRows.map((barcode) => barcode.barcode);
      await deleteBarcodesByID(barcodesToDelete);
      this.setState({ selectedRows: [], newSplitBarcodes: [] });
      await this.loadData();
    } catch (error) {
      this.context.updateSnack({ show: true, color: "red", message: error });
    } finally {
      this.setState({ loading: false, confirmDelete: false });
    }
  };

  handleSubmitForm = (values) => {
    this.setState({ newBarcode: values.barcode, newGrossWeight: values.new_weight });

    const newBarcodeExists = this.state.stock.reduce((arr, item) => {
      if (item.barcode.toString().trim() == values.barcode.toString().trim()) {
        const itemInList = this.state.newSplitBarcodes.find((row) => this.fruitSpecCompare(row, item));
        if (!itemInList) {
          arr.push({ ...item, exists: true });
        }
      }
      return arr;
    }, []);

    if (newBarcodeExists.length > 0) {
      this.setState({ barcodeExistsConfirmationOpen: true, existingBarcodeData: newBarcodeExists });
    } else {
      this.handleApplyBarcode({ barcode: values.barcode, newWeight: values.new_weight }, this.state.newSplitBarcodes);
    }
  };

  render() {
    const { classes } = this.state;

    if (this.state.submittingNewStock || this.state.loading) {
      return <LinearProgress color="secondary" style={{ marginBottom: "5px" }} />;
    }

    return (
      <div className={classes.root}>
        <DialogInformation
          showinput={false}
          title="Generate Stock Confirmation"
          isOpen={this.state.weightRequiredDialog}
          body={"Missing data! Gross weight is required in the data."}
          handleOK={() => this.setState({ weightRequiredDialog: false })}
          handleClose={() => this.setState({ weightRequiredDialog: false })}
        />
        <ConfirmationDialog
          isOpen={this.state.isOpen}
          handleClose={this.handleCloseConfirmation}
          handleConfirm={this.handleConfirmConfirmation}
          title="Generate Stock Confirmation"
          body={"There are data with no cartons to split. Is this correct?"}
        />
        <ConfirmationDialog
          isOpen={this.state.barcodeExistsConfirmationOpen}
          handleClose={this.handleCloseBarcodeExistsConfirmation}
          handleConfirm={this.handleConfirmBarcodeExistsConfirmation}
          title="Barcode exists"
          body={"Data with this barcode exists, would you like to add this to your current list?"}
        />
        <ConfirmationDialog
          isOpen={this.state.confirmDelete}
          handleClose={this.handleCloseConfirmDelete}
          handleConfirm={this.handleConfirmDeleteSelectedBarcodes}
          title="Confirm Delete"
          body={"Are you sure you want to delete the selected barcodes?"}
        />
        <ConfirmationDialog
          fullscreen
          body={""}
          disableBackdropClick
          isOpen={this.state.showSummaryView}
          handleClose={this.handleCloseSummaryView}
          handleConfirm={this.handleCloseSummaryView}
          title="New Barcodes Split Summary"
        >
          <SummaryView newSplitBarcodes={this.state.summaryData} handleClose={this.handleCloseSummaryView} />
        </ConfirmationDialog>
        <div id="main_splitter_div" style={{ height: "100%", width: "100%", display: "grid" }}>
          <Splitter
            postPoned
            dispatchResize
            position="horizontal"
            primaryPaneHeight="calc(100% - 600px)"
            primaryPaneMaxHeight="calc(100% - 150px)"
            onDragFinished={(e) => this.setGridHeights()}
          >
            <div className={classes.gridWrapper}>
              <div className={classes.gridToolbar}>
                <BarcodeSplitForm
                  handleSubmit={this.handleSubmitForm}
                  submitGenerateStock={this.submitGenerateStock}
                  submitDisabled={this.state.loading || this.state.newSplitBarcodes.length === 0}
                />
              </div>
              <Grid
                clearFilters={"barcodesplitnew"}
                GridColumns={NewGridColumns}
                loading={this.state.loading}
                data={this.state.newSplitBarcodes}
                forceHeight={this.state.minGridHeight}
                rowRenderer={(props) => this.newBarcodesRowRenderer(props, classes)}
                totalRowColumns={["noCartons", "grossWeight", "weight", "cartons_to_split", "new_weight", "palletSize"]}
              />
            </div>
            <div style={{ position: "relative", height: "100%" }}>
              <AppBar position="static">
                <Tabs value={this.state.tabIndex} onChange={this.handleTabChange} aria-label="Stock Allocation">
                  <Tab label="Available" {...this.a11yProps(availableItems_tabIndex)} />
                  <Tab
                    label={
                      <Badge badgeContent={this.state.selectedRows.length} color="secondary">
                        Selected
                      </Badge>
                    }
                    {...this.a11yProps(selectedItems_tabIndex)}
                  />
                </Tabs>
              </AppBar>
              <div hidden={this.state.tabIndex == 0 ? false : true} className={classes.toolbarWrapper}>
                <Toolbar className={classes.toolbar}>
                  <OrangeButton onClick={this.availableSelectAll} variant="contained" disabled={this.state.stock.length === 0}>
                    Select All
                  </OrangeButton>
                  <OrangeButton onClick={this.clearAvailableSelectedRows} variant="contained" disabled={this.state.selectedRows.length === 0}>
                    Clear Selected
                  </OrangeButton>
                  <RedButton onClick={this.handleDeleteSelectedBarcodes} variant="contained" disabled={this.state.selectedRows.length === 0}>
                    Delete Selected
                  </RedButton>
                </Toolbar>
                <Grid
                  clearFilters={"barcodesplit"}
                  data={this.state.stock}
                  GridColumns={GridColumns}
                  loading={this.state.loading}
                  forceHeight={this.state.minGridHeightSecond}
                  totalRowColumns={["noCartons", "grossWeight", "palletSize"]}
                  rowRenderer={(props) => this.rowRenderer(props, classes)}
                  handleFilteredRows={this.handleAvailableFilteredRows}
                />
              </div>
              <div hidden={this.state.tabIndex == 1 ? false : true}>
                <div className={classes.gridWrapper}>
                  <div className={classes.gridToolbar}>
                    <OrangeButton variant="contained" onClick={this.clearSelected} style={{ float: "right" }} disabled={this.state.selectedRows.length === 0}>
                      Clear Selected
                    </OrangeButton>
                  </div>
                  <Grid
                    fill
                    clearFilters={"barcodesplitedit"}
                    GridColumns={EditGridColumns}
                    loading={this.state.loading}
                    data={this.state.selectedRows}
                    handleChange={this.handleChange}
                    forceHeight={this.state.minGridHeightSecond}
                    selectedRowChange={this.handleSelectedRowChange}
                    handleGridKeyDown={this.handleKeyDown}
                    rowRenderer={(props) => this.editRowRenderer(props, classes)}
                    totalRowColumns={["noCartons", "grossWeight", "weight", "cartons_to_split", "new_weight", "palletSize"]}
                  />
                </div>
              </div>
            </div>
          </Splitter>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(BarcodeSplitUnstyled);
